import { Component } from 'react'
import { graphql } from '@apollo/client/react/hoc'
import { Table } from "reactstrap"
import toTitleCase from "../Functions/toTitleCase"
import { AllPromotionsTemplates } from "./Queries"
import EditPromotionModal from "./EditPromotionModal"
import PrimaryListingContainer from "../Material/PrimaryListingContainer"
import { StatusChip, VisibilityChip, formatDateRange } from "./ListingHelpers"
import "./ListingTable.css"
import { useAdvancedFilters } from "../Filters/filterUrl"
import { combineFilters } from "../Filters/presetFilters"


const tableHeaders = [
  { id: "pk", name: <i className="fa fa-square-o" aria-hidden="true"></i>, sortable: false },
  { id: "details", name: "Promo Details", sortable: false },
  { id: "vehicle", name: "Vehicle Eligibility", sortable: false },
  { id: "value", name: "Value & Cadence", sortable: false },
  { id: "schedule", name: "Schedule", sortable: false },
  { id: "status", name: "Status", sortable: false },
  { id: "visibleToCustomers", name: "Customer Visibility", sortable: false },
  { id: "action", name: "Action", sortable: false },
]

export class PromoTable extends Component {
  constructor(props) {
    super(props)
    this.state = {
      loading: false,
      openModal: "",
      expandedRows: new Set(),
    }
  }

  toggleModal = (name) => {
    this.setState({ openModal: this.state.openModal === name ? "" : name })
  };
  toggleExpand = (id) => {
    this.setState((prevState) => {
      const expandedRows = new Set(prevState.expandedRows)
      expandedRows.has(id) ? expandedRows.delete(id) : expandedRows.add(id)
      return { expandedRows }
    })
  };

  render() {
    return (
      <PrimaryListingContainer listLoading={this.props.loading || this.state.loading} totalFetchedRows={this.props.allPromotionsTemplates && this.props.allPromotionsTemplates.edges.length} pageInfo={this.props.allPromotionsTemplates && this.props.allPromotionsTemplates.pageInfo} loadMoreEntries={this.props.allPromotionsTemplates && this.props.allPromotionsTemplates.pageInfo.hasNextPage ? this.props.loadMoreEntries : null} refetchListQuery={this.props.refetchQuery}>
        <Table responsive className="listing-table">
          <thead>
            <tr>
              {tableHeaders.map(header => <th key={header.id} id={header.id}>{header.name}</th>)}
            </tr>
          </thead>
          <tbody>
            {this.props.allPromotionsTemplates && this.props.allPromotionsTemplates.edges && this.props.allPromotionsTemplates.edges.length > 0 ? this.props.allPromotionsTemplates.edges.map((promo, index) => {
              const node = promo.node
              const tagNames = (node.tags || []).map(tag => tag.name)
              const isExpanded = this.state.expandedRows.has(node.id)
              const isPercentage = !!node.isPercentage
              const branchName = node.agreementType?.branch?.name || "--"
              const agreementName = node.agreementType ? node.agreementType.name : "--"

              return (
                <tr key={node.id} id={node.id}>
                  <td scope="row">{index + 1}</td>
                  <td>
                    <span className="listing-identity__meta">
                        <span title="Branch">{branchName}</span> — <span title="Agreement Type">{agreementName}</span>
                    </span>
                    <span className="listing-identity__title" title="Title">{node.title || "--"}</span>
                    {tagNames.length > 0 &&
                        <div className="listing-identity__tags">
                            {tagNames.map(tag => <span key={tag} className="listing-identity__tag">{tag}</span>)}
                        </div>
                    }
                  </td>
                  <td>
                    {node.carModel ? <>
                      <span className="listing-vehicle__group" title="Car Group">{node.carModel.groupType ? node.carModel.groupType.name : "--"}</span>
                      <span className="listing-vehicle__model">{toTitleCase(node.carModel.make || "") + " " + toTitleCase(node.carModel.name)}{node.carModel.series && <> <span className="listing-vehicle__series" title={node.carModel.series}>({node.carModel.series})</span></>}</span>
                    </> : <span className="listing-vehicle__model">For All Models</span>}
                    <div className="listing-vehicle__fact">Year <b>{node.carYearMin ?? "Any"} — {node.carYearMax ?? "Any"}</b></div>
                  </td>
                  <td>
                    <span className="listing-value">
                      <span className="listing-value__amount">{isPercentage ? `${node.promoPrice}%` : `$${node.promoPrice}`}</span>
                      <span className="listing-value__meta">/ {node.interval} {node.intervalUnit} · cycle {node.cycle}</span>
                    </span>
                    <button type="button" className={"listing-expand-toggle" + (isExpanded ? " is-open" : "")} onClick={() => this.toggleExpand(node.id)}>
                      <i className={"fa " + (isExpanded ? "fa-angle-down" : "fa-angle-right")} aria-hidden="true"></i>
                      Range details
                    </button>
                    {isExpanded &&
                      <div className="listing-expand-panel">
                        <div className="listing-vehicle__fact">Value range <b>{isPercentage ? `${node.minimumPromoPrice}% — ${node.maximumPromoPrice}%` : `$${node.minimumPromoPrice} — $${node.maximumPromoPrice}`}</b></div>
                        <div className="listing-vehicle__fact">Cycle range <b>{node.minCycle} — {node.maxCycle}</b></div>
                      </div>
                    }
                  </td>
                  <td>
                    <span className="listing-schedule">
                      <i className="fa fa-calendar-o" aria-hidden="true"></i>
                      {formatDateRange(node.startDate, node.expirationDate)}
                    </span>
                  </td>
                  <td>
                    <div className="listing-status-cluster">
                      <StatusChip on={node.isActive} onText="Active" offText="Inactive" />
                    </div>
                  </td>
                  <td>
                    <div className="listing-status-cluster">
                      <VisibilityChip visible={node.visibleToCustomers} />
                    </div>
                  </td>
                  <td>
                    <i className={"fa fa-pencil fa-lg"} onClick={() => this.toggleModal("editPromo" + promo.node.pk)} id={"editPromo" + promo.node.pk}></i>
                    {this.state.openModal === "editPromo" + promo.node.pk &&
                      <EditPromotionModal
                        target={"editPromo" + promo.node.pk}
                        isOpen={this.state.openModal === "editPromo" + promo.node.pk}
                        promo={node}
                        refetchQuery={() => this.props.refetchQuery()}
                        handleClose={() => this.toggleModal("editPromo" + promo.node.pk)}
                      />
                    }
                  </td>
                </tr>
              )
            }) : !this.props.loading && <tr>
              <td colSpan={20}><p className="column-text">No Templates Found!</p></td>
            </tr>
            }
          </tbody>
        </Table>
      </PrimaryListingContainer>
    )
  }
}


const PromoTableWithData = graphql(AllPromotionsTemplates, {
  options: ({ searchTerm, orderBy, filters }) => ({
    variables: { searchTerm, orderBy, filters, first: 30 },
    notifyOnNetworkStatusChange: true, fetchPolicy: "cache-and-network",
  }), props: ({ data: { loading, allPromotionsTemplates, fetchMore, variables } }) => (
    {
      loading, allPromotionsTemplates, variables,
      refetchQuery: () => {
        let currentLength = allPromotionsTemplates && allPromotionsTemplates.edges ? allPromotionsTemplates.edges.length : 30
        return fetchMore({
          query: AllPromotionsTemplates,
          variables: {
            ...variables,
            first: currentLength,
          },
          updateQuery: (previousResult, { fetchMoreResult }) => {
            const newEdges = fetchMoreResult.allPromotionsTemplates.edges
            const pageInfo = fetchMoreResult.allPromotionsTemplates.pageInfo
            return {
              allPromotionsTemplates: {
                edges: [...newEdges],
                pageInfo,
                searchTerm: previousResult.searchTerm,
                __typename: previousResult.allPromotionsTemplates.__typename
              },
            }
          },
        })
      },

      loadMoreEntries: () => {
        return fetchMore({
          query: AllPromotionsTemplates,
          variables: {
            cursor: allPromotionsTemplates.pageInfo.endCursor,
            ...variables,
          },
          updateQuery: (previousResult, { fetchMoreResult }) => {
            const newEdges = fetchMoreResult.allPromotionsTemplates.edges
            const pageInfo = fetchMoreResult.allPromotionsTemplates.pageInfo

            return {
              allPromotionsTemplates: {
                edges: [
                  ...previousResult.allPromotionsTemplates.edges,
                  ...newEdges,
                ],
                pageInfo,
                status: previousResult.status,
                __typename: previousResult.allPromotionsTemplates.__typename,
              },
            }
          },
        })
      },
    }
  )
})(PromoTable)

// The container owns the editable is_active preset (session state); advanced filters
// live in the URL. Combine both into the single `filters` variable the query sends.
export default function PromoTableWithFilters(props) {
  const [advancedFilters] = useAdvancedFilters()
  return <PromoTableWithData {...props} filters={combineFilters(props.presets, advancedFilters)} />
}
